function money(v: any): string {
  const n = Number(v ?? 0);
  if (isNaN(n)) return '0.00';
  return n.toFixed(2);
}

function line(label: string, value: string, width = 32): string {
  const left = (label ?? '').trim();
  const right = (value ?? '').trim();
  const space = Math.max(1, width - left.length - right.length);
  return left + ' '.repeat(space) + right;
}

function divider(width = 32): string {
  return '-'.repeat(width);
}

function safeUpper(v: any): string {
  return String(v ?? '')
    .trim()
    .toUpperCase();
}

export function buildReceiptText(opts: {
  tenantName: string;
  companyNumber?: string | null;
  details: any;
}): string {
  const { tenantName, companyNumber, details } = opts;

  const publicCode = details?.publicOrderCode ?? '';
  const created =
    details?.createdAtDisplay ??
    details?.createdAt ??
    details?.created ??
    details?.dateCreated ??
    '';

  const orderTypeRaw = String(details?.orderType ?? '').toLowerCase();
  const orderType = orderTypeRaw === 'collection' ? 'COLLECTION' : 'DELIVERY';

  const paid = String(details?.paymentStatus ?? '').toLowerCase();
  const isPaid = ['paid', 'captured', 'succeeded', 'partial_refund'].includes(
    paid,
  );

  const first = (details?.firstName ?? '').trim();
  const customerName = `${first}`.trim();

  const phone = details?.phoneNumber ?? '';

  const delivery = details?.delivery ?? null;

  const items: any[] = Array.isArray(details?.details) ? details.details : [];

  // These vary by backend shape — keep flexible:
  const subtotal =
    details?.subTotal ??
    details?.subtotal ??
    details?.totalBeforeFees ??
    details?.totalBeforeFeesPrice ??
    null;

  const deliveryFee = details?.deliveryFee ?? details?.delivery_fee ?? 0;
  const orderFee = details?.orderFee ?? details?.order_fee ?? 0;
  const smallOrderFee = details?.smallOrderFee ?? 0;

  const total =
    details?.totalOrderModifiedPrice ??
    details?.total ??
    details?.totalPrice ??
    0;

  const lines: string[] = [];

  // Header
  lines.push(safeUpper(tenantName));
  if (companyNumber) lines.push(`Company No: ${companyNumber}`);
  lines.push(divider());

  // BIG ORDER TYPE
  lines.push(`*** ${orderType} ***`);
  lines.push(divider());

  // Meta
  if (publicCode) lines.push(`Code: ${publicCode}`);
  if (created) lines.push(`Time: ${created}`);
  lines.push(`Paid: ${isPaid ? 'YES' : 'NO'}`);
  lines.push(divider());

  // Delivery address (only if delivery)
  if (orderType === 'DELIVERY' && delivery) {
    lines.push('Delivery address:');
    if (delivery.address_line_1) lines.push(String(delivery.address_line_1));
    if (delivery.address_line_2) lines.push(String(delivery.address_line_2));
    const cityLine = [delivery.city, delivery.county]
      .filter(Boolean)
      .join(', ');
    if (cityLine) lines.push(cityLine);
    const postLine = [delivery.country, delivery.postal_code]
      .filter(Boolean)
      .join(' ');
    if (postLine) lines.push(postLine);
    lines.push(divider());
  }

  // Customer details
  if (customerName) lines.push(`Customer: ${customerName}`);
  if (phone) lines.push(`Phone: ${phone}`);
  lines.push(divider());

  // Items
  for (const it of items) {
    const name = String(it?.itemName ?? 'Item');
    const qty = Number(it?.itemQuantity ?? 1);
    const price = it?.itemModifiedPrice ?? 0;

    lines.push(`${qty} x ${name}`);
    lines.push(line(' ', `£${money(price)}`));

    // Extras (if present)
    const extras: any[] = Array.isArray(it?.extras) ? it.extras : [];
    if (extras.length) {
      for (const ex of extras) {
        const exName = String(ex?.extraName ?? 'Extra');
        const exQty = Number(ex?.extraQuantity ?? 1);
        const exPrice = ex?.extraPrice ?? 0;

        // Indented
        const exLabel = `  + ${exQty} x ${exName}`;
        const exValue = exPrice ? `£${money(exPrice)}` : '';
        lines.push(exValue ? line(exLabel, exValue) : exLabel);
      }
    }

    // Item note
    const note = (it?.itemNote ?? '').toString().trim();
    if (note) {
      lines.push(`  Note: ${note}`);
    }
  }

  // Totals
  lines.push(divider());
  if (subtotal != null) lines.push(line('Subtotal', `£${money(subtotal)}`));

  // Only show fees when they exist (and orderType makes sense)
  if (Number(deliveryFee) > 0)
    lines.push(line('Delivery fee', `£${money(deliveryFee)}`));
  if (Number(orderFee) > 0)
    lines.push(line('Order fee', `£${money(orderFee)}`));

  if (Number(smallOrderFee) > 0)
    lines.push(line('Small order fee', `£${money(smallOrderFee)}`));

  lines.push(line('TOTAL', `£${money(total)}`));
  lines.push(divider());

  // Order note
  const comment = (details?.comment ?? '').toString().trim();
  if (comment) {
    lines.push('Customer note:');
    lines.push(comment);
    lines.push(divider());
  }

  // Footer
  lines.push('Thank you!');
  lines.push('Powered by Waitron');
  lines.push('\n');

  return lines.join('\n');
}
